import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const MIN_VISIBLE_MS = 3000;
const MAX_VISIBLE_MS = 3500;
const FADE_MS = 400;

/**
 * Dismisses the pre-React splash screen rendered in index.html once the app
 * shell is ready (auth resolved + first route paint). Runs once per cold load;
 * in-app navigation never re-shows it because the node is removed from the DOM.
 */
export function useAppSplash() {
  const { loading } = useAuth();

  useEffect(() => {
    const el = document.getElementById('app-splash');
    if (!el) return;

    let removed = false;
    const startedAt = Number(el.dataset.startedAt || Date.now());

    const remove = () => {
      if (removed) return;
      removed = true;
      el.classList.add('app-splash--hidden');
      window.setTimeout(() => el.remove(), FADE_MS + 50);
    };

    // Hard safety net: never trap the user behind the splash.
    const hardTimer = window.setTimeout(remove, MAX_VISIBLE_MS);

    if (loading) {
      return () => window.clearTimeout(hardTimer);
    }

    // Auth is resolved — wait for the route to actually paint, then respect
    // the minimum on-screen time so the splash never blinks.
    let rafId = 0;
    let minTimer = 0;
    rafId = window.requestAnimationFrame(() => {
      rafId = window.requestAnimationFrame(() => {
        const elapsed = Date.now() - startedAt;
        minTimer = window.setTimeout(remove, Math.max(0, MIN_VISIBLE_MS - elapsed));
      });
    });

    return () => {
      window.clearTimeout(hardTimer);
      window.clearTimeout(minTimer);
      window.cancelAnimationFrame(rafId);
    };
  }, [loading]);
}
